import { usersQueryKey } from "@apis:user/user";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useLogin, useUser } from "@libs:react-query-auth";
import { Divider, InputAdornment, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useToasterStoreActions } from "@stores:global";
import { removeBorder } from "@utils:css";
import { FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";

import { DEFAULT_ROUTE_AFTER_LOGIN } from "@:constants";

export function Login() {
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const navigate = useNavigate();
  const user = useUser();

  const login = useLogin({
    onSuccess: async () => {
      await invalidateQueries.run([
        {
          queryKey: usersQueryKey.me.queryKey,
        },
      ]);
      navigate(DEFAULT_ROUTE_AFTER_LOGIN, {
        replace: true,
      });
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: "Failed to login",
        variant: "error",
      });
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    await login.mutateAsync({
      username: data.get("username") as string,
      password: data.get("password") as string,
    });
  };

  if (user.data) {
    return <Navigate to={DEFAULT_ROUTE_AFTER_LOGIN} replace />;
  }

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          boxShadow: "0px 8px 24px 0px #00000026",
          borderRadius: 6,
          padding: "40px",
          width: "380px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <SVGInternalIcon
            icon="nemoIcon"
            sx={{
              width: "120px",
              height: "25px",
            }}
          />
        </Box>
        <Typography
          variant="body1"
          sx={{
            marginTop: "1.7rem",
            fontWeight: "700",
            fontSize: "16px",
            lineHeight: "32px",
          }}
        >
          Log in to continue
        </Typography>
        <Box component="form" onSubmit={(e) => handleSubmit(e)}>
          <TextField
            data-cy="email"
            id="username"
            name="username"
            placeholder="Email"
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SVGInternalIcon
                      icon="botIcon"
                      sx={{
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              marginTop: "1.25rem",
              height: "2.5rem",
              background: "#F3F4F6E2",
              borderRadius: "23px",
              backgroundColor: "#EEEDF3",
              "&:hover": {
                backgroundColor: "#e6e6ea",
              },
              ...removeBorder,
            }}
          />
          <TextField
            data-cy="password"
            id="password"
            name="password"
            placeholder="Password"
            fullWidth
            required
            type="password"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SVGInternalIcon
                      icon="lockIcon"
                      sx={{
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              marginTop: "1.25rem",
              height: "2.5rem",
              background: "#F3F4F6E2",
              borderRadius: "23px",
              backgroundColor: "#EEEDF3",
              "&:hover": {
                backgroundColor: "#e6e6ea",
                borderWidth: 0,
              },
              ...removeBorder,
            }}
          />
          <Button
            data-cy="submit"
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              height: "39px",
              borderRadius: "23px",
              marginTop: "1.75rem",
              fontWeight: "700",
              fontSize: "14px",
              backgroundColor: "#1FADB2",
              color: "white",
              "&:hover": {
                backgroundColor: "#147275",
              },
            }}
          >
            Login
          </Button>
        </Box>
        <Divider
          variant="middle"
          flexItem
          sx={{
            marginTop: "2.5rem",
            marginBottom: "1.75rem",
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <SVGInternalIcon
            icon="innovumIcon"
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              filter: "grayscale(100%)",
            }}
          />
          <Typography
            component="h6"
            variant="h6"
            color="#555D77"
            sx={{
              fontWeight: "700",
            }}
          >
            INNOVUM
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
